/**
 * The state behind a searchable, paged list.
 *
 * Three things have to stay in step and are easy to get wrong separately:
 *
 * 1. **The URL is the state.** A filtered page must survive a refresh, a back button and being
 *    pasted to a colleague. So `q` and `page` live in the query string, not in component state.
 * 2. **The box updates instantly, the server does not.** What the user types is echoed with no
 *    delay; the request waits until they stop. A search that fires per keystroke sends eight
 *    requests for "adaeze" and renders whichever comes back last.
 * 3. **A new search means page one.** Staying on page 4 while the result set shrinks to six rows
 *    shows an empty table and reads as "no results", which is the opposite of what happened.
 *
 * The debounced value is what belongs in a query key; `text` is what belongs in the input.
 */

import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

const DEBOUNCE_MS = 250;

export interface ListParams {
  /** What is in the box right now. Bind the input to this. */
  text: string;
  /** What the server should be asked for. Put this in the query key. */
  q: string;
  page: number;
  setText: (next: string) => void;
  setPage: (next: number) => void;
  clear: () => void;
  /** True while the box and the request disagree — the moment to show a quiet spinner. */
  pending: boolean;
}

export function useListParams(): ListParams {
  const [params, setParams] = useSearchParams();
  const urlQ = params.get("q") ?? "";
  const page = Math.max(1, Number(params.get("page")) || 1);

  const [text, setText] = useState(urlQ);
  const typed = useRef(false);

  // The URL can change without the box — a back button, a link from elsewhere. Follow it, unless
  // the user is mid-type, in which case following would fight them for the caret.
  useEffect(() => {
    if (!typed.current) setText(urlQ);
  }, [urlQ]);

  useEffect(() => {
    if (text === urlQ) return;
    const timer = window.setTimeout(() => {
      typed.current = false;
      setParams(
        (current) => {
          const next = new URLSearchParams(current);
          if (text) next.set("q", text);
          else next.delete("q");
          next.delete("page"); // a new search starts at the beginning
          return next;
        },
        { replace: true }, // typing must not fill the back stack with one entry per letter
      );
    }, DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [text, urlQ, setParams]);

  return {
    text,
    q: urlQ,
    page,
    pending: text !== urlQ,
    setText: (next) => {
      typed.current = true;
      setText(next);
    },
    setPage: (next) =>
      setParams((current) => {
        const params = new URLSearchParams(current);
        if (next <= 1) params.delete("page");
        else params.set("page", String(next));
        return params;
      }),
    clear: () => {
      typed.current = false;
      setText("");
      setParams((current) => {
        const params = new URLSearchParams(current);
        params.delete("q");
        params.delete("page");
        return params;
      });
    },
  };
}

/** What the API returns alongside a paged list. */
export interface PageMeta {
  total: number;
  page: number;
  perPage: number;
  pages: number;
}

/**
 * Paging a list that is already in memory.
 *
 * The dashboard is one request that returns everything it shows, so its lists have nothing to
 * ask the server for. What they still need is a page size — twenty-three overdue invoices push
 * everything else below the fold, which defeats the point of a dashboard.
 *
 * Deliberately **not** in the URL, unlike `useListParams`. A dashboard is a glance, not a place
 * you link somebody to page three of, and putting it in the query string would remount the page
 * on every click of the pager.
 */
export function useLocalPage<T>(items: readonly T[], perPage: number): {
  rows: T[];
  meta: PageMeta;
  params: ListParams;
} {
  const [page, setPage] = useState(1);
  const pages = Math.max(1, Math.ceil(items.length / perPage));
  // The list can shrink under a pager sitting on a page that no longer exists — after a payment
  // clears the last overdue invoice, say. Clamp rather than render an empty page.
  const current = Math.min(page, pages);
  const start = (current - 1) * perPage;

  return {
    rows: items.slice(start, start + perPage) as T[],
    meta: { total: items.length, page: current, perPage, pages },
    params: {
      // Only `setPage` is read by `Pagination`; the rest exists so one component can serve both
      // kinds of list rather than growing a second, near-identical copy.
      text: "",
      q: "",
      page: current,
      setText: () => {},
      setPage,
      clear: () => setPage(1),
      pending: false,
    },
  };
}
